export const accountViews = {
  botConfig: {
    breadcrumb: {
      listings: "Listings",
      current: "AI Agent",
    },
    loading: "Loading configuration...",
    retry: "Retry",
    loadError: "Error loading the configuration.",
    units: "{count} unit | {count} units",
    faqsLabel: "FAQs",
    unitFallback: "Unit",
    active: "Active",
    inactive: "Inactive",
    botWhatsapp: "WhatsApp Bot",
    otaIa: "OTA AI",
    saving: "Saving...",
    cancel: "Cancel",
    assistant: {
      title: "WhatsApp Assistant",
      desc: "Turn on the automatic bot to reply to your guests on WhatsApp.",
    },
    ota: {
      title: "OTA AI",
      desc: "Automatically reply to messages from Booking.com, Airbnb and other OTAs.",
      warn: "Turn on the WhatsApp Assistant first",
    },
    botName: {
      title: "Assistant name",
      desc: "The name the bot will use to introduce itself to your guests.",
      empty: "No name assigned",
      edit: "Edit",
      placeholder: "E.g. María, Sofía, Virtual Assistant...",
      save: "Save",
    },
    faqs: {
      title: "Frequently asked questions",
      desc: "These FAQs apply to <strong>all units</strong> in this listing. The bot will use them to reply to your guests.",
      add: "New FAQ",
      emptyText: "You don't have any frequently asked questions set up yet.",
      addFirst: "+ Add first FAQ",
      answerEmpty: "No answer",
      editTitle: "Edit",
      deleteTitle: "Delete",
      questionLabel: "Question",
      answerLabel: "Answer",
      questionPlaceholder: "Type the question...",
      answerPlaceholder: "Type the answer the guest will see...",
      saveChanges: "Save changes",
    },
    addModal: {
      title: "New frequently asked question",
      questionLabel: "Question",
      questionPlaceholder: "E.g. What time is check-in?",
      answerLabel: "Answer",
      answerPlaceholder: "E.g. Check-in is from 3:00 PM. If you arrive earlier, you can leave your luggage...",
      submit: "Add FAQ",
    },
    deleteModal: {
      title: "Delete question",
      confirm: "Are you sure you want to delete this FAQ?",
      deleting: "Deleting...",
      submit: "Yes, delete",
    },
    types: {
      apartment: "Apartment",
      house: "House",
      villa: "Villa",
      hotel: "Hotel",
      room: "Room",
      studio: "Studio",
      default: "Accommodation",
    },
    toast: {
      botOn: "Bot enabled",
      botOff: "Bot disabled",
      botError: "Error changing the bot status",
      otaOn: "OTA AI enabled",
      otaOff: "OTA AI disabled",
      otaError: "Error changing the OTA AI status",
      nameUpdated: "Assistant name updated",
      nameError: "Error saving the name",
      faqAdded: "FAQ added successfully",
      faqAddError: "Error saving the FAQ",
      faqUpdated: "FAQ updated",
      faqUpdateError: "Error updating the FAQ",
      faqDeleted: "FAQ deleted",
      faqDeleteError: "Error deleting the FAQ",
    },
  },

  listingIndex: {
    title: "Listings",
    subtitle: "Manage the central configuration of each of your listings.",
    countFiltered: "{shown} of {total} listings",
    count: "{count} listing | {count} listings",
    searchPlaceholder: "Search by name, ID or type…",
    clear: "Clear",
    loadErrorTitle: "Couldn't load the accommodations",
    retry: "Retry",
    emptyTitle: "No listings",
    emptyText: "You don't have any listings registered yet.",
    noResultsTitle: 'No results for "{query}"',
    noResultsText: "Try another name, ID or listing type.",
    clearSearch: "Clear search",
    dragSearchHint: "Clear the search to reorder",
    dragHint: "Drag to reorder",
    units: "{count} unit | {count} units",
    botActiveTitle: "GPT bot active",
    channelTitle: "Connected to Channex",
    icalTitle: "iCal sync",
    editTitle: "Edit name",
    modal: {
      title: "Edit listing name",
      nameLabel: "Listing name",
      namePlaceholder: "E.g. Costa Sol Apartments",
      orderLabel: "Listing order",
      info: "A <strong>listing</strong> groups all the properties that share the same configuration, welcome messages and policies. The name you set here will identify the group in the panel and in the connected distribution channels.",
      cancel: "Cancel",
      save: "Save changes",
    },
    types: {
      apartment: "Apartment",
      house: "House",
      villa: "Villa",
      hotel: "Hotel",
      room: "Room",
      studio: "Studio",
      default: "Accommodation",
    },
    toast: {
      connectionError: "Connection error.",
      reorderSuccess: "Order updated successfully.",
      reorderError: "Couldn't update the listing order.",
      saveSuccess: "Listing updated successfully.",
      saveError: "Error saving the listing.",
    },
  },

  unitDetail: {
    back: "Back to listing",
    eyebrow: "Unit",
    save: "Save",
    cancel: "Cancel",
    editNameTitle: "Edit unit name",
    nameFallback: "Unit",
    types: {
      apartment: "Apartment",
      house: "House",
      villa: "Villa",
      hotel: "Hotel",
      camping: "Camping",
      room: "Room",
      studio: "Studio",
      default: "Accommodation",
    },
    deleteLink: "Delete unit...",
    dangerNoteBefore: "Removes the unit from the listing",
    dangerNoteAfter: "from WACheckin. This action can't be undone.",
    cards: {
      description: { title: "Description", sub: "Unit details" },
      capacity: { title: "Capacity", sub: "Guests and beds" },
      services: { title: "Equipment and amenities", sub: "What it offers, with photos" },
      rules: { title: "Schedules", sub: "Check-in and check-out times" },
      arrival: { title: "Arrival Instructions", sub: "How to get there and get in · with send time" },
      smartlocks: { title: "Locks and access", sub: "How the guest gets in" },
      checkout: { title: "Checkout message", sub: "Checkout reminder · template" },
      scheduled: { title: "Scheduled messages", sub: "Messages for this unit only" },
      unitFaq: { title: "Questions and answers", sub: "Specific to this unit for the agent", badge: "AI" },
      welcome: { title: "Welcome message", sub: "Message specific to this unit" },
      stockExtra: {
        title: "Extra stock",
        subOff: "Off · picks up bookings from another unit",
        subOn: "Backs up {name}",
        subOnGeneric: "Backs up another unit",
        badge: "On",
      },
    },
    sections: {
      aiOnlyLabel: "Unit's frequently asked questions",
      aiOnlyDesc: "The agent will prioritize them when assisting a guest staying in this unit.",
      specificLabel: "Settings specific to this unit",
      detailLabel: "Detail this unit",
      detailDesc: "Additional information we use for direct bookings and to feed the AI Agent.",
    },
    stockExtra: {
      title: "Extra stock",
      intro: "On Booking or Airbnb you may have a single listing with room for several apartments: since only one can be mapped, the channel sends all those bookings against the same unit and they pile up as overbooking.",
      how: "If you mark this unit as extra stock of another one, bookings that clash with the main unit are saved here and the channel is told so that they show up in this unit there too.",
      purpose: "It is a moving tool: turn it on to bring over what you had already sold on the OTA, and turn it off once every apartment has its own listing.",
      loading: "Loading...",
      activeLabel: "Backs up {name}",
      activeHint: "Channel bookings that clash with {name} are saved in this unit.",
      activeBadge: "On",
      otherUnit: "another unit",
      warnTitle: "Don't turn it on if every apartment already has its own listing mapped on the OTA:",
      warnText: "there the channel already sends each booking to its own unit, and with extra stock on a booking would end up in an apartment that isn't the one you sold.",
      principalOf: "Other units already lean on this one:",
      chooseLabel: "Main unit",
      chooseDesc: "Choose the unit whose bookings will be saved in \"{unit}\" when they clash with each other.",
      noCandidates: "There is no other unit to back up in this listing.",
      activate: "Turn on extra stock",
      change: "Change the main unit",
      saving: "Saving...",
      removeBtn: "Remove extra stock",
      removeNote: "Bookings already saved here are not moved: they stay in this unit.",
      errorCarga: "Could not load the extra stock settings. If you have just updated, try reloading the page.",
      reintentar: "Try again",
      toastLoadError: "Extra stock could not be loaded.",
      toastSaveError: "Extra stock could not be saved.",
    },
    toast: {
      nameEmpty: "The name can't be empty",
      nameUpdated: "Name updated",
      nameError: "Couldn't update the name",
    },
  },

  miCuenta: {},
};
